import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/context/AuthProvider";
import { useCompanyContext } from "../../common/company/CompanyContext";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import CompanyIdentityHeader from "../../common/company/CompanyIdentityHeader";
import DeclrLocalDeclarationController from "./DeclrLocalDeclarationController";

/**
 * 🎯 LOCAL DECLARATION ENTRY
 * The company's actual data-capture screen — one type-driven input per
 * attribute, grouped into the sections built on the Attribute Groups
 * screen, for a chosen reporting year. Values stay in local units here;
 * group-level roll-up happens later via Attribute Mapping.
 */
export default function DeclrLocalDeclarationView() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { activeCompanyId, activeCompanyName } = useCompanyContext();
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const SITE_NAME = process.env.REACT_APP_SITE_NAME || "sustina.nrgpix.ch";

  const {
    reportingYear,
    setReportingYear,
    groups,
    values,
    unitsForFamily,
    enumOptionsByAttr,
    loading,
    savingId,
    error,
    handleFieldChange,
    handleSaveField,
  } = DeclrLocalDeclarationController(activeCompanyId, siteKey);

  const handleSignOut = async () => {
    await logout();
    navigate("/auth/sign-in");
  };

  if (!activeCompanyId) {
    navigate("/registered/dashboard");
    return null;
  }

  const renderInput = (member) => {
    const entry = values[member.AttributeDefinitionId] || {};
    const commonSave = () => handleSaveField(member.AttributeDefinitionId, member.DefaultUnitCode);

    switch (member.ValueDataType) {
      case "numeric":
        return (
          <div className="d-flex gap-2">
            <input
              type="number"
              className="form-control form-control-sm"
              value={entry.EnteredValue ?? ""}
              onChange={(e) => handleFieldChange(member.AttributeDefinitionId, "EnteredValue", e.target.value)}
              onBlur={commonSave}
            />
            {member.AllowedUnitFamily && (
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 120 }}
                value={entry.EnteredUnitCode || member.DefaultUnitCode || ""}
                onChange={(e) => {
                  handleFieldChange(member.AttributeDefinitionId, "EnteredUnitCode", e.target.value);
                  commonSave();
                }}
              >
                {unitsForFamily(member.AllowedUnitFamily).map((u) => {
                  const code = GoldenSourceHelper.get(u, "UnitCode");
                  return <option key={code} value={code}>{code}</option>;
                })}
              </select>
            )}
          </div>
        );

      case "boolean":
        return (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 160 }}
            value={entry.EnteredValue ?? ""}
            onChange={(e) => {
              handleFieldChange(member.AttributeDefinitionId, "EnteredValue", e.target.value);
              handleSaveField(member.AttributeDefinitionId, member.DefaultUnitCode);
            }}
          >
            <option value="">-- select --</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      case "enum": {
        const options = enumOptionsByAttr[member.AttributeDefinitionId] || [];
        return (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 220 }}
            value={entry.EnteredValue ?? ""}
            onChange={(e) => {
              handleFieldChange(member.AttributeDefinitionId, "EnteredValue", e.target.value);
              handleSaveField(member.AttributeDefinitionId, member.DefaultUnitCode);
            }}
          >
            <option value="">-- select --</option>
            {options.map((opt) => {
              const val = GoldenSourceHelper.get(opt, "OptionValue");
              return <option key={val} value={val}>{GoldenSourceHelper.get(opt, "OptionLabel")}</option>;
            })}
          </select>
        );
      }

      case "date":
        return (
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ maxWidth: 180 }}
            value={entry.EnteredValue ?? ""}
            onChange={(e) => handleFieldChange(member.AttributeDefinitionId, "EnteredValue", e.target.value)}
            onBlur={commonSave}
          />
        );

      case "text":
      default:
        return (
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={entry.EnteredValue ?? ""}
            onChange={(e) => handleFieldChange(member.AttributeDefinitionId, "EnteredValue", e.target.value)}
            onBlur={commonSave}
          />
        );
    }
  };

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2 align-items-end">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Local Declaration</h2>
                <p className="text-muted small mb-0">enter this company's ESG data, captured in local units</p>
              </div>
              <div className="col-auto">
                <label className="extra-small fw-bold text-muted d-block mb-1">Reporting Year</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  style={{ width: 110 }}
                  value={reportingYear}
                  onChange={(e) => setReportingYear(Number(e.target.value))}
                />
              </div>
            </div>

            <CompanyIdentityHeader companyId={activeCompanyId} siteKey={siteKey} />

            {error && <div className="alert alert-danger extra-small">{error}</div>}

            {loading ? (
              <p className="text-muted extra-small italic">Loading...</p>
            ) : groups.length === 0 ? (
              <p className="text-muted extra-small italic">
                No attribute groups yet — build one on the Attribute Groups screen first.
              </p>
            ) : (
              groups.map((group) => (
                <div className="card shadow-sm border-0 bg-white overflow-hidden mb-3" key={group.AttributeGroupId}>
                  <div className="p-3 border-bottom bg-light">
                    <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">{group.GroupLabel}</h6>
                  </div>
                  <div className="p-3">
                    {group.members.length === 0 && (
                      <p className="text-muted extra-small italic mb-0">No attributes in this group.</p>
                    )}
                    {group.members.map((member) => (
                      <div className="row align-items-center py-2 border-bottom" key={member.AttributeDefinitionId}>
                        <div className="col-md-4">
                          <span className="extra-small fw-bold">{member.AttributeLabel}</span>
                          {savingId === member.AttributeDefinitionId && (
                            <span className="extra-small text-muted ms-2">saving...</span>
                          )}
                        </div>
                        <div className="col-md-8">{renderInput(member)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3">
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={() => navigate("/registered/company-dashboard")}>
                  <i className="bi bi-arrow-left me-2"></i> Back to Dashboard
                </button>
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={handleSignOut}>
                  <i className="bi bi-box-arrow-left me-2"></i> Sign Out
                </button>
              </div>
              <div className="text-end">
                <span className="text-muted extra-small d-block">Workflow Context</span>
                <span className="fw-bold small">{activeCompanyName || SITE_NAME}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
